import type { ReactElement, ReactNode } from 'react';
import { useRef, useState, useLayoutEffect } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import {
  Separator,
  Button,
  Collection,
  Focusable,
  Group,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
} from 'react-aria-components';
import cx from 'clsx';
import { useIntl } from 'react-intl';
import { IconChevronBottomOutlined } from '@repo/monochrome-icons';
import type { Placement } from 'react-aria';
import btnStyles from '../../primitives/button.module.css';
import { Spinner } from '../spinner';
import { Tooltip } from '../tooltip';
import styles from './dropdown-button.module.css';

interface ItemShape {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  dataTestId?: string;
  icon?: ReactNode;
  badge?: ReactNode;
  className?: string;
  /** When 'destructive', item text uses --content-error (e.g. "Remove widget"). */
  variant?: 'default' | 'destructive';
}

export interface BaseDropdownButtonProps {
  dataTestId?: string;
  isLoading?: boolean;
  isMainDisabled?: boolean;
  isDropdownDisabled?: boolean;
  triggerDataTestId?: string;
  variant?: 'primary' | 'secondary';
  onOpenChange?: (isOpen: boolean) => void;
  placement?: Placement;
  items: ItemShape[];
  sidenavItems?: ItemShape[];
  customMenu?: ReactNode;
  iconOnly?: boolean;
  portalContainerId?: string;
  tooltipAriaLabel?: string;
  tooltipLabel?: string;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  tooltipSize?: 'small' | 'large';
  dropdownFullWidth?: boolean;
  mainTooltip?: {
    isDisabled?: boolean;
    label: string;
    placement?: 'top' | 'bottom' | 'left' | 'right';
  };
}

interface DropdownButtonProps extends AriaButtonProps, BaseDropdownButtonProps {
  children: ReactNode;
  style?: React.CSSProperties;
}

function MenuItemCollection({ items }: { items: ItemShape[] }): ReactNode {
  return (
    <Collection items={items}>
      {item => (
        <MenuItem
          className={cx(
            styles.menuItem,
            item.variant === 'destructive' && styles.menuItemDestructive,
            item.className
          )}
          data-testid={item.dataTestId}
          id={item.label}
          isDisabled={item.disabled}
          key={item.label}
          onAction={item.onSelect}
        >
          <div className={styles.itemWrapper}>
            {Boolean(item.icon) && <span className={styles.iconWrapper}>{item.icon}</span>}
            {item.label}
            {Boolean(item.badge) && <span className={styles.badgeWrapper}>{item.badge}</span>}
          </div>
        </MenuItem>
      )}
    </Collection>
  );
}

export function DropdownButton({
  children,
  isLoading = false,
  isDisabled = false,
  isMainDisabled = false,
  isDropdownDisabled = false,
  items,
  sidenavItems,
  className,
  style,
  dataTestId,
  triggerDataTestId,
  variant = 'primary',
  customMenu,
  onOpenChange,
  placement = 'bottom start',
  portalContainerId,
  iconOnly = false,
  tooltipAriaLabel,
  tooltipLabel,
  tooltipPlacement = 'bottom',
  tooltipSize = 'large',
  dropdownFullWidth = false,
  mainTooltip,
  ...props
}: DropdownButtonProps): ReactElement {
  const intl = useIntl();
  const mainButtonRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);

  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER =
    ((portalContainerId ? document.getElementById(portalContainerId) : undefined) ||
      document.getElementById('ember-testing')) ??
    undefined;

  useLayoutEffect(() => {
    if (mainButtonRef.current && dropdownFullWidth) {
      setTriggerWidth(mainButtonRef.current.offsetWidth);
    }
  }, [dropdownFullWidth, children]);

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  return (
    <div
      className={cx(
        'body-standard',
        styles.container,
        variant === 'secondary' && styles.secondary,
        className
      )}
      ref={mainButtonRef}
      style={style}
    >
      <Group className={styles.buttonGroup} isDisabled={isDisabled}>
        {!iconOnly && (
          <Tooltip
            isDisabled={mainTooltip ? mainTooltip.isDisabled : true}
            label={mainTooltip?.label || 'Button'}
            placement={mainTooltip?.placement ?? 'bottom'}
            tooltipTestId={dataTestId}
          >
            <Focusable>
              <div className={styles.focusableWrapper} data-testid="pay-button-tooltip">
                <Button
                  className={cx(
                    btnStyles.btn,
                    btnStyles.primary,
                    btnStyles['btn--small'],
                    styles.mainButton,
                    (items.length > 0 || customMenu) && styles.mainButtonBorders
                  )}
                  data-testid={dataTestId}
                  isDisabled={isDisabled || isMainDisabled || isLoading}
                  {...props}
                >
                  <div className={styles.buttonContent}>{children}</div>
                  {isLoading ? <Spinner className={styles.spinner} /> : null}
                </Button>
              </div>
            </Focusable>
          </Tooltip>
        )}

        {items.length > 0 || customMenu ? (
          <MenuTrigger onOpenChange={handleOpenChange}>
            {iconOnly ? (
              <Tooltip
                ariaLabel={tooltipAriaLabel || 'Menu'}
                buttonProps={{
                  iconOnly: true,
                  variant: 'tertiary',
                  size: tooltipSize,
                  isDisabled: isDisabled || isDropdownDisabled || isLoading,
                  ...props,
                }}
                isDisabled={!tooltipAriaLabel && !tooltipLabel}
                label={tooltipLabel || 'Menu'}
                needsButton
                placement={tooltipPlacement}
                tooltipTestId={dataTestId}
              >
                {children}
              </Tooltip>
            ) : (
              <Button
                aria-label={
                  isOpen
                    ? intl.formatMessage({ id: 'a11y-buttons-close-aria-label' })
                    : intl.formatMessage({ id: 'a11y-buttons-open-aria-label' })
                }
                className={cx(
                  btnStyles.btn,
                  btnStyles.primary,
                  btnStyles['btn--small'],
                  styles.triggerButton
                )}
                data-testid={triggerDataTestId ?? `${dataTestId}-trigger`}
                isDisabled={isDisabled || isDropdownDisabled || isLoading}
              >
                <IconChevronBottomOutlined
                  className={cx(styles.arrowIcon, isOpen && styles.arrowIconRotated)}
                />
              </Button>
            )}

            <Popover
              UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
              className={cx(
                'body-standard',
                styles.popover,
                dropdownFullWidth && styles.popoverFullWidth
              )}
              offset={4}
              placement={placement}
              style={dropdownFullWidth && triggerWidth ? { width: triggerWidth } : undefined}
              triggerRef={mainButtonRef}
            >
              {customMenu ?? (
                <Menu className={styles.menu} data-testid={`${dataTestId}-menu`}>
                  <MenuItemCollection items={items} />
                  {Array.isArray(sidenavItems) && sidenavItems.length > 0 && (
                    <>
                      <Separator className={styles.separator} />
                      <MenuItemCollection items={sidenavItems} />
                    </>
                  )}
                </Menu>
              )}
            </Popover>
          </MenuTrigger>
        ) : null}
      </Group>
    </div>
  );
}
